import React from 'react';
import { ArrowLeft, CheckCircle, Eye, EyeOff, Plus, Search, ShoppingCart, Store, Trash2, Users, XCircle } from 'lucide-react';
import { fuzzyMatch } from '../../../utils/helpers';

const TableOrderModal = ({ categories, currentOrder, handleAddItemToOrder, handleRemoveItem, productSearchQuery, products, selectedCategory, selectedTable, setCurrentOrder, setIsCloseOrderModalOpen, setIsOrderModalOpen, setProductSearchQuery, setSelectedCategory, setSelectedTable, setShowProductImages, showProductImages, tables }) => {
  return (
        <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-gray-900 to-slate-900 z-[9999] flex flex-col" data-testid="order-modal">
          {/* Header - Compacto e Discreto */}
          <div className="bg-gradient-to-r from-orange-600 to-orange-500 px-3 sm:px-5 py-2 sm:py-3 shadow-lg flex-shrink-0">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="bg-white/20 backdrop-blur-sm rounded-full p-1.5 flex-shrink-0">
                  <Store className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-sm sm:text-lg font-bold text-white truncate">{selectedTable.name}</h2>
                  <p className="text-orange-100 text-xs flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    <span className="hidden sm:inline">{selectedTable.capacity} lugares</span>
                    <span className="sm:hidden">{selectedTable.capacity}</span>
                  </p>
                </div>
              </div>
              
              {/* Quick Stats - Inline e Compacto */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg px-2 py-1">
                  <p className="text-orange-100 text-[10px] leading-none">Itens</p>
                  <p className="text-white font-bold text-sm leading-tight">{currentOrder.items?.length || 0}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg px-2 py-1">
                  <p className="text-orange-100 text-[10px] leading-none">Total</p>
                  <p className="text-white font-bold text-sm leading-tight">R$ {currentOrder.total?.toFixed(2) || '0.00'}</p>
                </div>
                <button
                  onClick={() => {
                    setIsOrderModalOpen(false);
                    setCurrentOrder(null);
                    setSelectedTable(null);
                    setProductSearchQuery('');
                  }}
                  className="w-8 h-8 sm:w-9 sm:h-9 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-all flex-shrink-0"
                  data-testid="close-order-modal-button"
                >
                  <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </button>
              </div>
            </div>
          </div>

          {/* Content - Two Column Layout for Desktop */}
          <div className="flex-1 overflow-y-auto flex flex-col lg:flex-row">
            {/* Left Side - Menu/Cardápio */}
            <div className="flex-1 bg-gradient-to-b from-slate-800 to-slate-900 p-5">
              {/* Section Title */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-6 bg-orange-500 rounded-full"></div>
                  <h3 className="text-white font-bold text-xl">Pede é Fácil</h3>
                </div>
                
                {/* Toggle Image Visibility - Discreto */}
                <button
                  onClick={() => setShowProductImages(!showProductImages)}
                  className="p-2 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg transition-all group"
                  title={showProductImages ? "Ocultar imagens" : "Mostrar imagens"}
                  data-testid="toggle-product-images"
                >
                  {showProductImages ? (
                    <Eye className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
                  )}
                </button>
              </div>
              
              {/* Category Pills - Horizontal scroll */}
              <div className="mb-5">
                <div className="flex gap-2 overflow-x-auto pb-3 hide-scrollbar">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`px-5 py-2.5 rounded-full whitespace-nowrap text-sm font-semibold transition-all flex-shrink-0 shadow-md ${
                      selectedCategory === 'all'
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-orange-500/50'
                        : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                    }`}
                    data-testid="category-all"
                  >
                    🍽️ Todos
                  </button>
                  {categories.filter(cat => cat.active).map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-5 py-2.5 rounded-full whitespace-nowrap text-sm font-semibold transition-all flex-shrink-0 shadow-md ${
                        selectedCategory === category.id
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-orange-500/50'
                          : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                      }`}
                      data-testid={`category-${category.id}`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Busca de produtos com lupa - tolerante a acentos e erros de digitação */}
              <div className="mb-5">
                <div className="relative">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                    placeholder="Buscar produto por nome..."
                    className="w-full pl-11 pr-11 py-3 bg-slate-700/60 border border-slate-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors"
                    data-testid="table-product-search"
                  />
                  {productSearchQuery && (
                    <button
                      onClick={() => setProductSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-slate-600 hover:bg-slate-500 transition-colors"
                      title="Limpar busca"
                    >
                      <XCircle className="w-4 h-4 text-gray-300" />
                    </button>
                  )}
                </div>
              </div>

              {/* Product Grid - Beautiful Cards */}
              {(() => {
                const visibleProducts = products
                  .filter(p => p.available)
                  .filter(p => selectedCategory === 'all' || p.category_id === selectedCategory)
                  .filter(p => !productSearchQuery.trim() || fuzzyMatch(productSearchQuery, `${p.name || ''} ${p.description || ''}`));
                if (productSearchQuery.trim() && visibleProducts.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <Search className="w-12 h-12 text-gray-600 mb-3" />
                      <p className="text-gray-400 font-medium">Nenhum produto encontrado para {`"${productSearchQuery}"`}</p>
                      <p className="text-gray-500 text-sm mt-1">Tente outra palavra ou verifique a categoria selecionada.</p>
                    </div>
                  );
                }
                return (
                <div className={`grid gap-4 pb-20 ${showProductImages ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                  {visibleProducts
                    .map((product) => (
                  <div
                    key={product.id}
                    className={`bg-gradient-to-br from-slate-700 to-slate-800 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 cursor-pointer group border border-slate-600/50 ${!showProductImages ? 'flex items-center' : ''}`}
                    onClick={() => handleAddItemToOrder(product.id, 1)}
                    data-testid={`add-product-${product.id}`}
                  >
                    {/* Product Image Placeholder - Condicional */}
                    {showProductImages && (
                      <div className="h-32 bg-gradient-to-br from-orange-500/20 to-orange-600/20 relative overflow-hidden">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-6xl opacity-30">🍽️</div>
                        </div>
                        {/* Quick Add Badge */}
                        <div className="absolute top-3 right-3">
                          <div className="bg-orange-500 text-white rounded-full p-2 shadow-lg group-hover:scale-110 transition-transform">
                            <Plus className="w-5 h-5" />
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className={`p-4 ${!showProductImages ? 'flex items-center justify-between w-full gap-4' : ''}`}>
                      <div className="flex-1">
                        <h4 className="text-white font-bold text-lg mb-1 line-clamp-1">{product.name}</h4>
                        {product.description && (
                          <p className="text-gray-400 text-sm mb-3 line-clamp-2 leading-relaxed">{product.description}</p>
                        )}
                      </div>
                      
                      <div className={`flex items-center ${showProductImages ? 'justify-between pt-2 border-t border-slate-600' : 'gap-4'}`}>
                        <span className="text-orange-400 font-bold text-xl">R$ {product.price.toFixed(2)}</span>
                        {!showProductImages && (
                          <div className="bg-orange-500 text-white rounded-full p-2 shadow-lg group-hover:scale-110 transition-transform flex-shrink-0">
                            <Plus className="w-5 h-5" />
                          </div>
                        )}
                        {showProductImages && (
                          <span className="text-gray-400 text-xs bg-slate-600/50 px-3 py-1 rounded-full">
                            Toque para adicionar
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
                );
              })()}
            </div>

            {/* Right Side - Order Summary (Pedido) */}
            <div className="lg:w-96 bg-slate-800 border-t lg:border-t-0 lg:border-l border-slate-700 flex flex-col">
              {/* Order Items Section */}
              {currentOrder.items && currentOrder.items.length > 0 ? (
                <>
                  <div className="p-5 border-b border-slate-700 flex-shrink-0">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-1 h-6 bg-green-500 rounded-full"></div>
                      <h3 className="text-white font-bold text-lg">Pedido Atual</h3>
                    </div>
                    <p className="text-gray-400 text-sm">{currentOrder.items.length} {currentOrder.items.length === 1 ? 'item' : 'itens'} adicionado{currentOrder.items.length === 1 ? '' : 's'}</p>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-4">
                    {currentOrder.items.map((item, index) => (
                      <div
                        key={item.id}
                        className="bg-slate-700/50 rounded-xl p-4 backdrop-blur-sm border border-slate-600/30 hover:border-slate-500 transition-all"
                        data-testid={`order-item-${item.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="bg-orange-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                                {index + 1}
                              </span>
                              <p className="text-white font-semibold">{item.product_name}</p>
                            </div>
                            <p className="text-gray-400 text-sm ml-8">
                              {item.quantity}x R$ {item.unit_price.toFixed(2)}
                            </p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveItem(item.id);
                            }}
                            className="w-9 h-9 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg flex items-center justify-center transition-all hover:scale-110"
                            data-testid={`remove-item-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex justify-end">
                          <span className="text-orange-400 font-bold text-lg">R$ {item.subtotal.toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Total Summary Card - Fixed at bottom */}
                  <div className="p-5 bg-slate-900 border-t border-slate-700 flex-shrink-0">
                    <div className="bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl p-4 mb-4 border border-slate-600">
                      <div className="space-y-2 mb-3">
                        <div className="flex justify-between text-gray-300 text-sm">
                          <span>Subtotal:</span>
                          <span data-testid="order-subtotal">R$ {currentOrder.subtotal?.toFixed(2) || '0.00'}</span>
                        </div>
                        <div className="flex justify-between text-gray-300 text-sm">
                          <span>Taxa de Serviço (10%):</span>
                          <span data-testid="order-service-fee">R$ {currentOrder.service_fee?.toFixed(2) || '0.00'}</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center pt-3 border-t border-slate-600">
                        <span className="text-white font-bold text-lg">Total:</span>
                        <span className="text-orange-400 font-bold text-2xl" data-testid="order-total">
                          R$ {currentOrder.total?.toFixed(2) || '0.00'}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                  <div className="bg-slate-700/30 rounded-full p-6 mb-4">
                    <ShoppingCart className="w-16 h-16 text-gray-500" />
                  </div>
                  <h3 className="text-white font-bold text-xl mb-2">Pedido Vazio</h3>
                  <p className="text-gray-400 text-sm">Selecione itens do cardápio para começar</p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons - Fixed at Bottom */}
          <div className="flex-shrink-0 bg-slate-900 border-t border-slate-700 px-5 py-4 shadow-2xl">
            <div className="flex gap-3 max-w-7xl mx-auto">
              <button
                onClick={() => {
                  setIsOrderModalOpen(false);
                  setCurrentOrder(null);
                  setSelectedTable(null);
                }}
                className="flex-1 px-6 py-4 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-all font-semibold flex items-center justify-center gap-2 shadow-lg"
                data-testid="back-to-tables-button"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-base">Voltar</span>
              </button>
              
              <button
                onClick={() => setIsCloseOrderModalOpen(true)}
                disabled={!currentOrder.items || currentOrder.items.length === 0}
                className="flex-1 px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl transition-all font-bold shadow-xl shadow-green-500/40 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="close-order-button"
              >
                <CheckCircle className="w-5 h-5" />
                <span className="text-base">Finalizar Pedido</span>
              </button>
            </div>
          </div>
        </div>
  );
};

export default TableOrderModal;
